import { IconButton, Tooltip } from "@mui/material";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { RiDownload2Line } from "react-icons/ri";
import * as XLSX from "xlsx";

const ExportAsXLSXButton = ({ data, label = "Download", filename = "download.xlsx", columnMapping = null }) => {
  const { t } = useTranslation();
  const handleExport = (rawData) => {
    if (!columnMapping) {
      const worksheet = XLSX.utils.json_to_sheet(rawData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);
      return;
    }

    const brandGreen = "#659d33";
    let xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="10"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
   </Borders>
  </Style>
  <Style ss:ID="MainHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="${brandGreen}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="RefHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#4B5563" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="UserHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="${brandGreen}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="TitleStyle">
   <Font ss:FontName="Segoe UI" ss:Size="16" ss:Bold="1" ss:Color="#0F172A"/>
  </Style>
  <Style ss:ID="MetaLabel">
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Bold="1" ss:Color="#475569"/>
  </Style>
  <Style ss:ID="MetaValue">
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#0F172A"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Environmental Factors">
  <Table>`;

    const headers = Object.entries(columnMapping);
    
    // Define column widths for 47 columns to fit content nicely
    xml += `
   <Column ss:Width="60"/>
   <Column ss:Width="150"/>
   <Column ss:Width="150"/>
   <Column ss:Width="150"/>
   <Column ss:Width="250"/>`;
   
    for (let i = 0; i < 42; i++) {
      xml += `\n   <Column ss:Width="120"/>`;
    }

    xml += `
   <Row ss:Height="30">
    <Cell ss:StyleID="TitleStyle"><Data ss:Type="String">Arivu Environmental Factors Report</Data></Cell>
   </Row>
   <Row ss:Height="20">
    <Cell ss:StyleID="MetaLabel"><Data ss:Type="String">Export Date:</Data></Cell>
    <Cell ss:StyleID="MetaValue"><Data ss:Type="String">${new Date().toLocaleDateString()}</Data></Cell>
   </Row>
   <Row ss:Height="10"></Row>
   <Row ss:Height="28">`;

    headers.forEach(([key, header]) => {
      let style = "MainHeader";
      const index = headers.findIndex(([k]) => k === key);
      if (index >= 5 && index <= 25) {
        style = "RefHeader";
      } else if (index > 25) {
        style = "UserHeader";
      }
      xml += `<Cell ss:StyleID="${style}"><Data ss:Type="String">${t(header)}</Data></Cell>`;
    });

    xml += `</Row>`;

    rawData.forEach((row) => {
      xml += `\n   <Row ss:Height="22">`;
      headers.forEach(([key]) => {
        let val = row[key];
        if (typeof val === "boolean") {
          val = val ? "Yes" : "No";
        }
        if (val === null || val === undefined) {
          val = "";
        }
        
        let type = "String";
        if (typeof val === "number" && !isNaN(val)) {
          type = "Number";
        }
        
        xml += `<Cell><Data ss:Type="${type}">${val}</Data></Cell>`;
      });
      xml += `</Row>`;
    });

    xml += `
  </Table>
 </Worksheet>
</Workbook>`;

    const blob = new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" });
    saveAs(blob, filename.replace(".xlsx", ".xls"));
  };
  return (
    <Tooltip title={t(label)}>
      <span>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleExport(data);
          }}
          sx={{
            backgroundColor: "rgba(22, 163, 74, 0.2)",
            "&:hover": { color: "white", backgroundColor: "rgba(22, 163, 74, 0.9)" },
            borderRadius: 2,
          }}>
          <RiDownload2Line />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ExportAsXLSXButton;
